import { useEffect, useRef, useState, FC } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'motion/react';

/* ── ICONS (inline SVG to avoid import deps) ── */
const Icon: FC<{ d: string; size?: number; className?: string; strokeWidth?: number; style?: React.CSSProperties }> = ({ d, size = 20, className = "", strokeWidth = 1.5, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    className={className} style={style}>
    <path d={d} />
  </svg>
);

const icons = {
  globe: "M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 0c-2.5 3-4 6.5-4 10s1.5 7 4 10m0-20c2.5 3 4 6.5 4 10s-1.5 7-4 10M2 12h20",
  check: "M20 6L9 17l-5-5",
  x: "M18 6L6 18M6 6l12 12",
  zap: "M13 2L3 14h9l-1 8 10-12h-9l1-8z",
  credit: "M2 6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6zm0 5h20",
  clock: "M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 6v4l3 3",
  alert: "M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z",
  package: "M16.5 9.4l-9-5.19M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16zM3.27 6.96L12 12.01l8.73-5.05M12 22.08V12",
  users: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2m22 0v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  dollar: "M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6",
  network: "M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18",
  file: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm4 6H14V2",
  eye: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zm11-3a3 3 0 1 0 0 6 3 3 0 0 0 0-6z",
  layers: "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",
  monitor: "M20 3H4a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1zM8 21h8m-4-4v4",
  target: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zm0-6a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0-2a2 2 0 1 0 0-4 2 2 0 0 0 0 4z",
  refresh: "M23 4v6h-6M1 20v-6h6m-.93-9A10 10 0 0 1 21.93 12a10 10 0 0 1-17.86 7",
  msg: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
  help: "M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3m.08 4h.01M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z",
  arrow: "M5 12h14m-7-7 7 7-7 7",
  settings: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm8.188-3A8.953 8.953 0 0 0 21 12a9 9 0 0 0-.812-3.766l-2.455.818A6.5 6.5 0 0 1 18 12a6.5 6.5 0 0 1-.267 1.948l2.455.818zM12 3a9 9 0 0 0-3.766.812l.818 2.455A6.5 6.5 0 0 1 12 6a6.5 6.5 0 0 1 1.948.267l.818-2.455A9 9 0 0 0 12 3z",
  shield: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  bar: "M12 20V10m6 10V4M6 20v-4",
  phone: "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z",
  trending: "M23 6l-9.5 9.5-5-5L1 18"
};

type IconName = keyof typeof icons;

/* ── Particle Canvas ── */
const ParticleCanvas: FC<{ color?: string; count?: number; maxDist?: number }> = ({ color = "#ff5d22", count = 60, maxDist = 130 }) => {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let w: number, h: number, pts: any[], raf: number;
    const init = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
      pts = Array.from({ length: count }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 1.5 + 0.4,
        pulse: Math.random() * Math.PI * 2,
      }));
    };
    const hex2rgb = (hStr: string) => [parseInt(hStr.slice(1,3),16), parseInt(hStr.slice(3,5),16), parseInt(hStr.slice(5,7),16)];
    const [r,g,b] = hex2rgb(color);
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.pulse += 0.015;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
      });
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const d = Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y);
          if (d < maxDist) {
            const alpha = (1 - d / maxDist) * 0.22;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(${r},${g},${b},${alpha})`;
            ctx.lineWidth = 0.6;
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.stroke();
          }
        }
      }
      pts.forEach(p => {
        const pr = p.r * (1 + 0.25 * Math.sin(p.pulse));
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, pr * 3);
        grad.addColorStop(0, `rgba(${r},${g},${b},0.85)`);
        grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.beginPath();
        ctx.arc(p.x, p.y, pr * 3, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    init(); draw();
    const ro = new ResizeObserver(init);
    ro.observe(canvas);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [color, count, maxDist]);
  return <canvas ref={ref} style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none" }} />;
};

/* ── 3D Grid Floor ── */
const GridFloor: FC<{ opacity?: number }> = ({ opacity = 0.06 }) => {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let w: number, h: number, raf: number, t = 0;
    const init = () => { w = canvas.width = canvas.offsetWidth; h = canvas.height = canvas.offsetHeight; };
    const draw = () => {
      t += 0.0025;
      ctx.clearRect(0, 0, w, h);
      const cols = 18, rows = 12;
      const cw = w / cols, ch = h / rows;
      for (let i = 0; i <= cols; i++) {
        const x = i * cw;
        const wave = Math.sin(i * 0.35 + t) * 6;
        ctx.beginPath();
        ctx.strokeStyle = `rgba(227,30,36,${opacity * (0.4 + 0.6 * Math.sin(i * 0.25 + t))})`;
        ctx.lineWidth = 0.5;
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h + wave);
        ctx.stroke();
      }
      for (let j = 0; j <= rows; j++) {
        const y = j * ch;
        const wave = Math.sin(j * 0.45 + t) * 4;
        ctx.beginPath();
        ctx.strokeStyle = `rgba(227,30,36,${opacity * (0.4 + 0.6 * Math.cos(j * 0.25 + t))})`;
        ctx.lineWidth = 0.5;
        ctx.moveTo(0, y + wave);
        ctx.lineTo(w, y + wave);
        ctx.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    init(); draw();
    const ro = new ResizeObserver(init);
    ro.observe(canvas);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [opacity]);
  return <canvas ref={ref} style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none" }} />;
};

/* ── Orb ── */
const Orb: FC<{ style?: React.CSSProperties; color: string; size: number; animDur: number; animDelay?: number }> = ({ style = {}, color, size, animDur, animDelay = 0 }) => (
  <div style={{
    position:"absolute", borderRadius:"50%", filter:`blur(${size*0.35}px)`,
    width:size, height:size, background:color, opacity:0.14,
    animation:`orbFloat ${animDur}s ease-in-out infinite`,
    animationDelay:`${animDelay}s`,
    pointerEvents:"none", zIndex:1,
    ...style
  }} />
);

/* ── Reveal ── */
const Reveal: FC<{ children: React.ReactNode; delay?: number; style?: React.CSSProperties; className?: string }> = ({ children, delay = 0, style = {}, className = "" }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={className} style={{
      opacity: vis ? 1 : 0,
      transform: vis ? "none" : "translateY(15px)",
      transition: `opacity 0.75s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.75s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      ...style
    }}>
      {children}
    </div>
  );
};

/* ── TiltCard ── */
const TiltCard: FC<{ children: React.ReactNode; className?: string; style?: React.CSSProperties }> = ({ children, className = "", style = {} }) => {
  const ref = useRef<HTMLDivElement>(null);
  const handleMove = (e: React.MouseEvent) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width - 0.5) * 12;
    const y = ((e.clientY - r.top) / r.height - 0.5) * -12;
    ref.current.style.transform = `perspective(800px) rotateY(${x}deg) rotateX(${y}deg) translateY(-4px)`;
  };
  const handleLeave = () => {
    if (!ref.current) return;
    ref.current.style.transform = "perspective(800px) rotateY(0) rotateX(0) translateY(0)";
  };
  return (
    <div ref={ref} onMouseMove={handleMove} onMouseLeave={handleLeave} className={`card-3d ${className}`} style={{ transformStyle:"preserve-3d", transition:"transform 0.35s cubic-bezier(0.1, 0.8, 0.2, 1)", ...style }}>
      {children}
    </div>
  );
};

/* ── FAQItem ── */
const FAQItem: FC<{ q: string; a: string; idx: number }> = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={idx * 0.05} className="mb-4">
      <div onClick={() => setOpen(!open)} className={`rounded-xl border border-slate-200 bg-white p-5 cursor-pointer transition-all duration-300 shadow-sm ${open ? 'ring-1 ring-[#ff5d22] border-[#ff5d22]/30 shadow-md' : 'hover:border-slate-300'}`}>
        <div className="flex justify-between items-center gap-4">
          <h3 className="font-bold text-sm md:text-base text-slate-900 leading-snug">{q}</h3>
          <div className={`w-6 h-6 rounded-full bg-[#ff5d22]/10 flex items-center justify-center flex-shrink-0 text-[#ff5d22] transition-transform duration-300 ${open ? 'rotate-180' : ''}`}>
            <Icon d="M19 9l-7 7-7-7" size={12} strokeWidth={2.5} />
          </div>
        </div>
        <div style={{ maxHeight: open ? 500 : 0, opacity: open ? 1 : 0, transition: "max-height 0.35s ease, opacity 0.3s ease, margin-top 0.3s ease", overflow: "hidden" }} className={open ? "mt-4" : ""}>
          <p className="text-slate-600 text-[13px] md:text-sm font-medium leading-relaxed">{a}</p>
        </div>
      </div>
    </Reveal>
  );
};

/* ── DATA ── */
const D = {
  stats: [
    { val: '99.999%', unit: 'BILLING INTEGRITY', label: 'Eliminating monetization leakage in high-concurrency CDR rating environments' },
    { val: '10x',  unit: 'SPEED TO GTM',       label: 'Launch dynamic international bundles, custom tariffs, and multi-currency lines seamlessly' },
    { val: '90%', unit: 'WORKFLOW BOOST',      label: 'Automation of downstream DID inventory pooling and switch activation' },
  ],
  problems: [
    { icon: 'network' as IconName,  title: 'Disparate Billing & Med', desc: 'Siloed systems confuse traditional voice CDRs, messaging payloads, and recurring user licensing, triggering revenue leakage.' },
    { icon: 'clock' as IconName,   title: 'Hyperscale Med Bottlenecks', desc: 'Finance teams waste days processing raw session logs and adjusting customer ledger balances manually at month end.' },
    { icon: 'alert' as IconName,   title: 'Onerous Telephony Taxing', desc: 'Generic billing suites fail to process nested USF, regulatory levies, and state/federal telecom tax rules across jurisdictions.' },
    { icon: 'dollar' as IconName,  title: 'Complex DID Administration', desc: 'Provisioning phone numbers, SIP trunks, and switch accounts manually takes days and creates immediate friction with GTM velocity.' },
  ],
  comparison: [
    { f: 'Converged Hybrid Billing', ours: 'Voice plans, CDR consumption, subscription hybrids', theirs: 'Fragile multiple systems patched together' },
    { f: 'Mediation Engine', ours: 'Real-time ingestion of millions of events per second', theirs: 'Batch exports with delayed file processing' },
    { f: 'DID Lifecycle Auto', ours: 'Native DID pool orchestration and switch sync', theirs: 'Manual registry updates or external custom work' },
    { f: 'Global Telephony Taxing', ours: 'Automated telco tax compliance across 160+ countries', theirs: 'Basic SaaS tax integrations without telecom logic' },
    { f: 'Dynamic Discounting', ours: 'Geo-based triggers, tier discounting, and wallet sync', theirs: 'Flat coupon systems requiring manual adjustments' },
  ],
  whyFeatures: [
    'Deploy nested mediation logic to ingest, normalize, and rate voice session CDRs instantly',
    'Structure convergent hybrid offerings combining per-seat licensing, pre-paid trunks, and rate-cards',
    'Orchestrate logical switch provisioning and active DID inventory gates directly via programmatic API controllers',
    'Completely eliminate month-end manual audits with autonomous, double-entry certified financial settlement ledgering',
    'Empower active enterprise accounts with self-serve portals containing live utilization data and billing thresholds'
  ],
  pillars: [
    { icon: 'zap' as IconName, title: 'Hyperscale Mediation', desc: 'Autonomously ingest raw call detail records from BroadSoft, Metaswitch, Asterisk, or Cisco switches and mediatize them on a zero-loss engine.' },
    { icon: 'credit' as IconName, title: 'Convergent Voice Commerce', desc: 'Harmonize recurring user fees, metered trunks, pre-paid wallet systems, and localized rates in one cohesive ledger stack.' },
    { icon: 'network' as IconName, title: 'Downstream Provisioner', desc: 'Connect billing statuses directly to programmatic activation rules — managing DID inventory, PBX accounts, and resellers.' }
  ],
  features: [
    { icon: 'eye' as IconName, title: 'Real-Time MediateX Ingest', desc: 'Autolog session CDRs and messaging packets with zero drop. Sanitize parameters and map fields to invoice-ready ratings.' },
    { icon: 'phone' as IconName, title: 'Bespoke Rating Matrix', desc: 'Deploy complex local and international roaming ratecards, peak/off-peak tier discounts, and hybrid user models in minutes.' },
    { icon: 'layers' as IconName, title: 'DID Pool Governance', desc: 'Administer global dynamic inventories natively. Port, purchase, activate, or block telephone numbers on automated workflows.' },
    { icon: 'shield' as IconName, title: 'Planet-Scale Compliance', desc: 'Incorporate live regulatory tax algorithms calculating localized USF, FCC, VAT, and fiscal elements accurately.' },
    { icon: 'refresh' as IconName, title: 'Unified Partner Settlement', desc: 'Disburse commissions, tier GTM margins, and reconcile multi-level distributor ledger paths automitacally.' },
    { icon: 'bar' as IconName, title: 'Cohesive Analytics Console', desc: 'Expose self-serve utilization metrics, transaction trends, and real-time ledger records for enterprise key partners.' }
  ],
  aiTags: [
    'CDR anomaly recognition',
    'Usage leakage prevention',
    'Dynamic tariff recommendations',
    'Downstream fraud detection',
    'Propensity and churn models',
    'Peak-load traffic forecasting',
    'Localized tax logic sync',
    'Natural language invoice search'
  ],
  platformFeatures: [
    { val: '100M/day', label: 'Ingestion capability', desc: 'Successfully processing extreme telecom session inputs with absolute zero data erosion.' },
    { val: '99.999%', label: 'Ledger exactness', desc: 'Underlined by carrier-grade transaction isolation to guard every cent of contract value.' },
    { val: '160+', label: 'Tax jurisdictions', desc: 'Dynamic tables auto-updated to handle local state, federal, and global telco taxation.' },
    { val: '90%', label: 'GTM acceleration', desc: 'Downstream provisioning cycles run in seconds instead of manual business weeks.' }
  ],
  results: [
    { val: '90%', label: 'Provisioning lead time contraction', org: 'Enterprise CCaaS, North American Market' },
    { val: '99%', label: 'Billing close accuracy audit score', org: 'Tier 1 Managed Provider, APAC Region' },
    { val: '75%', label: 'Reduction in overall client invoice queries', org: 'SmartFlo (Tata Tele), Unified Cloud Services' }
  ],
  faq: [
    {
      q: 'Does AppGallop support real-time voice CDR ingestion and mediation?',
      a: 'Yes. Our proprietary MediateX engine ingests raw call detail records (CDRs) from major softswitches, validates packet structure, and rates each call session in milliseconds, completely bypassing traditional multi-day reconciliation lags.'
    },
    {
      q: 'Can we build hybrid converged billing structures (e.g., license seats + voice minute rates)?',
      a: 'Absolutely. AppGallop is built specifically for convergent commerce. You can combine seat license pricing, pre-paid wallet credits, post-paid minutes usage, tiered volume discounts, and DID rental fees into a unified single invoice.'
    },
    {
      q: 'How does programmatic DID inventory orchestration integrate with active gateways?',
      a: 'AppGallop interacts directly via REST interfaces with active telephony suppliers and switches. When an enterprise account buys and maps voice bundles, numbers are leased, assigned, and switch configs deployed instantly.'
    },
    {
      q: 'How does the platform handle telco taxation across countries?',
      a: 'AppGallop incorporates specialized compliance rules calculating federal, state, and local taxes, specialized regulatory fees, and USF payments across 160+ global borders out-of-the-box, updating values dynamically.'
    }
  ]
};

interface UCBillingPageProps {
  data: any;
  theme: any;
  ProductHero: React.ComponentType<any>;
  ContactSection: React.ComponentType<any>;
  SectionHeader: React.ComponentType<any>;
  MockDashboard: React.ComponentType<any>;
  Button: React.ComponentType<any>;
}

export const UCBillingPageContent: FC<UCBillingPageProps> = ({ ContactSection }) => {
  return (
    <div className="bg-white overflow-x-hidden w-full">
      <style>{`
        @keyframes orbFloat { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(40px,-30px) scale(1.08)} 66%{transform:translate(-25px,20px) scale(0.95)} }
        @keyframes pulse3d  { 0%,100%{box-shadow:0 0 0 0 rgba(227,30,36,0.4)} 50%{box-shadow:0 0 0 20px rgba(227,30,36,0)} }
        .card-3d { transition:transform 0.3s ease,box-shadow 0.3s ease }
        .card-3d:hover { transform:translateY(-8px);box-shadow:0 32px 64px rgba(0,0,0,0.18) }
      `}</style>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex flex-col justify-center bg-gradient-to-b from-white via-slate-50 to-white overflow-hidden px-6" style={{ paddingTop:'120px', paddingBottom:'60px' }}>
        <ParticleCanvas count={65} color="#ff5d22" />
        <GridFloor opacity={0.03} />
        <Orb style={{ top:'-5%', right:'-8%' }} color="#ff5d22" size={420} animDur={18} />
        <Orb style={{ bottom:'-10%', left:'-5%' }} color="#3b82f6" size={370} animDur={22} animDelay={3} />

        <div className="relative z-20 max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/5 text-slate-700 text-[9px] font-black uppercase tracking-widest mb-4 border border-slate-200">
                <svg width={8} height={8} viewBox="0 0 10 10"><circle cx={5} cy={5} r={5} fill="#ff5d22" /></svg>
                CPaaS & CCaaS Commerce Engine
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.1] text-slate-950 mb-4">
                The convergent billing platform{' '}
                <span className="bg-gradient-to-r from-[#ff5d22] to-red-600 bg-clip-text text-transparent">
                  built for global telcos
                </span>
              </h1>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-6 max-w-lg font-medium">
                Siloed voice detail systems, rigid subscriptions, and complex taxation overhead choke telecom commerce scale. AppGallop MediateX unifies session mediation, charging, and DID provisioning into one seamless lead-to-ledger lattice.
              </p>
            </Reveal>
            <Reveal delay={0.20}>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact">
                  <button className="px-6 py-2.5 rounded-lg bg-[#ff5d22] text-white font-black text-[10px] uppercase tracking-widest border-none cursor-pointer hover:bg-[#d41820] transition-all duration-300" style={{ animation:'pulse3d 3s ease infinite' }}>
                    Schedule Call
                  </button>
                </Link>
                <Link to="/solutions">
                  <button className="px-6 py-2.5 rounded-lg bg-white text-slate-700 font-black text-[10px] uppercase tracking-widest border border-slate-300 cursor-pointer hover:bg-slate-50 transition-all duration-300">
                    Explore Platform
                  </button>
                </Link>
              </div>
            </Reveal>
          </div>

          {/* Stats Cluster */}
          <div className="grid gap-4 w-full">
            {D.stats.map((s, i) => (
              <Reveal key={i} delay={0.1 + i * 0.08}>
                <TiltCard className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/40 to-transparent" />
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="text-2xl md:text-3xl font-black text-[#2563EB] leading-none">{s.val}</div>
                      <div className="text-[8px] text-slate-400 font-black uppercase tracking-wider mt-1">{s.unit}</div>
                    </div>
                    <div className="w-px h-10 bg-slate-200" />
                    <div className="text-xs md:text-sm text-slate-600 font-medium leading-relaxed">{s.label}</div>
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEMS (slate-950 dark) */}
      <section className="relative bg-slate-950 py-20 px-6 overflow-hidden">
        <ParticleCanvas count={50} color="#ff5d22" />
        <Orb style={{ top:'15%', right:'-8%' }} color="#ff5d22" size={350} animDur={20} />
        <div className="relative z-20 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[9px] font-black uppercase tracking-widest mb-4">
                The Telecom Operational Ceiling
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-tight text-white mb-3">
                Billing leakage is the{' '}
                <span className="text-[#ff5d22]">silent margin killer</span>
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="text-sm text-white/50 max-w-xl mx-auto leading-relaxed font-medium">
                As service providers roll out modern cloud services, legacy convergent pipelines drop millions in unregistered minutes, manual rating, and custom offline billing spreadsheets.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {D.problems.map((item, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <TiltCard className="bg-white/3 border border-white/8 rounded-lg p-6 backdrop-blur-xl relative overflow-hidden h-full">
                  <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#ff5d22]/50 to-transparent" />
                  <div className="w-10 h-10 rounded-lg bg-[#ff5d22]/15 border border-[#ff5d22]/30 flex items-center justify-center mb-3">
                    <Icon d={icons[item.icon]} size={18} style={{ color:'#ff5d22' }} />
                  </div>
                  <h3 className="text-base font-black text-white mb-2 tracking-tight">{item.title}</h3>
                  <p className="text-xs text-white/55 leading-relaxed font-medium">{item.desc}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON (slate-50) */}
      <section className="relative bg-slate-50 py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(rgba(227,30,36,0.03)_1px,transparent_1px)] bg-[length:30px_30px]" />
        <Orb style={{ top:'10%', right:'5%' }} color="#ff5d22" size={280} animDur={18} />
        <div className="relative z-20 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                AppGallop vs Legacy Billing
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-0">
                Purpose-built converged logic —{' '}
                <span className="text-[#ff5d22]">not stitched together</span>
              </h2>
            </Reveal>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-12 bg-slate-50/80 border-b border-slate-100 p-5 items-center">
              <div className="col-span-4 pl-4">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Core Capability</span>
              </div>
              <div className="col-span-4 flex justify-center">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-slate-300" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Legacy Billing</span>
                </div>
              </div>
              <div className="col-span-4 flex justify-center">
                <div className="flex items-center gap-2 relative">
                  <div className="w-2 h-2 rounded-full bg-[#ff5d22] shadow-sm shadow-[#ff5d22]/50" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#ff5d22]">AppGallop</span>                  
                </div>
              </div>
            </div>

            {/* Table Body */}
            <div className="flex flex-col">
              {D.comparison.map((c, i) => (
                <Reveal key={i} delay={0.1 + i * 0.05}>
                  <div className={`grid grid-cols-1 md:grid-cols-12 p-5 gap-4 md:gap-0 items-center transition-colors hover:bg-slate-50/50 ${i < D.comparison.length - 1 ? 'border-b border-slate-100' : ''}`}>
                    {/* Feature Name */}
                    <div className="col-span-12 md:col-span-4 md:pl-4">
                      <span className="font-bold text-slate-800 text-xs md:text-[13px]">{c.f}</span>
                    </div>
                    
                    {/* Theirs */}
                    <div className="col-span-12 md:col-span-4 flex items-center gap-3 justify-start md:justify-center">
                      <div className="w-6 h-6 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                        <Icon d={icons.x} size={10} style={{ color:'#ef4444' }} strokeWidth={2.5} />
                      </div>
                      <span className="text-slate-500 font-medium text-xs md:text-center leading-tight max-w-[210px]">{c.theirs}</span>
                    </div>

                    {/* Ours */}
                    <div className="col-span-12 md:col-span-4 flex items-center gap-3 justify-start md:justify-center">
                      <div className="w-6 h-6 rounded-full bg-[#ff5d22]/10 flex items-center justify-center flex-shrink-0 relative">
                        <div className="absolute inset-0 bg-[#ff5d22]/20 rounded-full blur-[2px]" />
                        <Icon d={icons.check} size={10} style={{ color:'#ff5d22', position: 'relative' }} strokeWidth={2.5} />
                      </div>
                      <span className="text-slate-800 font-bold text-xs md:text-center leading-tight max-w-[210px]">{c.ours}</span>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY US (white, 2-col) */}
      <section className="relative bg-white py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[length:26px_26px]" />
        <Orb style={{ bottom:'0%', left:'0%' }} color="#ff5d22" size={350} animDur={22} />
        <div className="relative z-10 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                Unified Telecom Lattice
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-tight mb-4 text-slate-900">
                Convergence engineered for{' '}
                <span className="text-[#ff5d22]">complex digital networks</span>
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed font-medium">
                Modern telephony demands sub-second event mediation and complex tariff rating out-of-the-box. AppGallop unifies direct, partner, and reseller paths to consolidate invoices, prevent usage leakage, and secure your ledger records in real time.
              </p>
            </Reveal>
          </div>
          <div className="flex flex-col gap-2">
            {D.whyFeatures.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="card-3d flex items-center gap-3 p-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md cursor-default">
                  <div className="w-8 h-8 rounded-lg bg-[#ff5d22]/8 border border-[#ff5d22]/20 flex items-center justify-center flex-shrink-0">
                    <Icon d={icons.check} size={14} style={{ color:'#ff5d22' }} strokeWidth={2.5} />
                  </div>
                  <span className="text-xs md:text-sm font-semibold text-slate-700 leading-relaxed">{f}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS (slate-950 dark, 3-col) */}
      <section className="relative bg-[#08080c] py-20 px-6 overflow-hidden">
        <ParticleCanvas count={50} color="#3b82f6" />
        <Orb style={{ top:'5%', left:'15%' }} color="#ff5d22" size={400} animDur={20} />
        <GridFloor opacity={0.04} />
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[9px] font-black uppercase tracking-widest mb-4">
                Three Strategic Pillars
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-tight text-white mb-3">
                Source. Charge. <span className="text-[#ff5d22]">Provision.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="text-[#98a2b3] text-sm md:text-base max-w-xl mx-auto leading-relaxed">
                Connect the disconnected silos. AppGallop handles complex SIP trunk switches, number storage registries, billing policies, and customer accounting natively.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {D.pillars.map((p, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <TiltCard className="bg-white/[0.02] border border-white/8 rounded-2xl p-8 text-center backdrop-blur-xl relative overflow-hidden h-full">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/40 to-transparent" />
                  <div className="relative display inline-block mb-4">
                    <div className="absolute inset-[-8px] rounded-full bg-[#ff5d22]/12 blur-lg" />
                    <div className="relative w-14 h-14 rounded-xl bg-[#ff5d22]/15 border border-[#ff5d22]/30 flex items-center justify-center mx-auto">
                      <Icon d={icons[p.icon]} size={22} style={{ color:'#ff5d22' }} />
                    </div>
                  </div>
                  <h3 className="text-lg font-black text-white mb-2">{p.title}</h3>
                  <p className="text-xs text-white/50 leading-relaxed font-medium">{p.desc}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GTM BANNER (red) */}
      <section className="relative bg-gradient-to-br from-[#ff5d22] via-red-600 to-red-800 py-16 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[length:24px_24px]" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/30 text-white text-[9px] font-black uppercase tracking-widest mb-4">
              Converged Logic. Planet Scale.
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-black tracking-tighter text-white mb-3">
              Automate multi-region voice and SaaS ledgers together
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="text-white/85 text-sm md:text-base max-w-xl mx-auto font-medium leading-relaxed">
              Consolidate SIP trunking contracts, down-to-the-millisecond consumption, and SaaS hybrid tiers into a governed commercial motion that requires zero manual auditing headcount.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FEATURES GRID (#fafafa) */}
      <section className="relative bg-[#fafafa] py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(rgba(227,30,36,0.025)_1px,transparent_1px)] bg-[length:28px_28px]" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                Platform Capabilities
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-0 text-slate-900">
                Engineered for <span className="text-[#ff5d22]">carrier-grade speed</span>
              </h2>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {D.features.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="card-3d bg-white border border-slate-200 rounded-2xl p-6 relative overflow-hidden h-full shadow-sm">
                  <div className="w-11 h-11 rounded-lg bg-[#ff5d22]/6 border border-[#ff5d22]/15 flex items-center justify-center mb-4">
                    <Icon d={icons[f.icon]} size={18} style={{ color:'#ff5d22' }} />
                  </div>
                  <h3 className="text-base font-black text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed font-semibold">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI CAPABILITIES (slate-950 dark) */}
      <section className="relative bg-slate-950 py-20 px-6 overflow-hidden">
        <ParticleCanvas count={50} color="#8b5cf6" />
        <Orb style={{ top:'-5%', right:'-8%' }} color="#ff5d22" size={400} animDur={19} />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[9px] font-black uppercase tracking-widest mb-4">
              AI Capabilities
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-white mb-4 leading-tight">
              AI optimization tailored for <span className="text-[#ff5d22]">high-traffic CDRs</span>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="text-slate-400 text-sm md:text-base leading-relaxed mb-8 max-w-xl mx-auto font-medium">
              Eliminate revenue leakage automatically. Our specialized AI modeling recognizes transaction anomalies, tags usage changes, and flags drop-offs instantly to prevent customer disputes.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex justify-center gap-2.5 flex-wrap">
              {D.aiTags.map((tag, i) => (
                <span key={i} className="px-4 py-2 rounded-full bg-white/5 border border-white/12 text-slate-300 text-xs font-semibold">
                  {tag}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CLARITY (modern split-screen) */}
      <section className="relative py-20 bg-gradient-to-br from-slate-50 via-white to-slate-100 overflow-hidden px-6">
        <ParticleCanvas count={40} color="#ff5d22" />
        <div className="absolute inset-0 bg-[radial-gradient(rgba(227,30,36,0.015)_1px,transparent_1px)] bg-[length:40px_40px]" />
        <Orb style={{ top:'10%', right:'-5%' }} color="#ff5d22" size={300} animDur={20} />
        <Orb style={{ bottom:'15%', left:'-8%' }} color="#3b82f6" size={250} animDur={25} animDelay={2} />

        <div className="relative z-10 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Side: Problems */}
          <div className="space-y-6">
            <Reveal>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-red-600 text-xs font-bold uppercase tracking-wider">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                The Problem
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter leading-tight text-slate-900">
                Telecom rating is{' '}
                <span className="relative">
                  <span className="text-red-500">leakage-heavy</span>
                  <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-red-200 rounded-full" />
                </span>
              </h2>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="grid gap-3">
                {[
                  'Fragmented CDR ingestion triggers lost packet records',
                  'Siloed switch logic delays rating computations',
                  'Failing to link usage gates dynamically causes unbilled margins',
                  'Manual auditing slows monthly close by weeks'
                ].map((problem, i) => (
                  <TiltCard key={i} className="flex items-start gap-3 p-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md">
                    <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon d={icons.x} size={10} style={{ color:'#dc2626' }} strokeWidth={2.5} />
                    </div>
                    <span className="text-xs md:text-sm font-semibold text-slate-600 leading-relaxed">{problem}</span>
                  </TiltCard>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Right Side: Solutions */}
          <div className="space-y-6">
            <Reveal delay={0.3}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-600 text-xs font-bold uppercase tracking-wider">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                The Solution
              </div>
            </Reveal>
            <Reveal delay={0.4}>
              <h2 className="text-3xl md:text-4xl font-black tracking-tighter leading-tight text-slate-900">
                AppGallop MediateX{' '}
                <span className="relative">
                  <span className="text-[#ff5d22]">solves it</span>
                  <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#ff5d22]/30 rounded-full" />
                </span>
              </h2>
            </Reveal>

            <Reveal delay={0.5}>
              <div className="grid gap-3">
                {[
                  'Unified atomic switch links capture 100% of CDR sessions',
                  'Convergent rating logic ratings compile in milliseconds',
                  'Auto DID inventory synchronizer blocks capacity leakages',
                  'Double-entry subledger engines complete auditing in hours'
                ].map((sol, i) => (
                  <TiltCard key={i} className="flex items-start gap-3 p-4 bg-gradient-to-r from-white to-slate-50 border border-[#ff5d22]/20 rounded-xl">
                    <div className="w-5 h-5 rounded-full bg-[#ff5d22]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon d={icons.check} size={10} style={{ color:'#ff5d22' }} strokeWidth={2.5} />
                    </div>
                    <span className="text-xs md:text-sm font-semibold text-slate-700 leading-relaxed">{sol}</span>
                  </TiltCard>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CASE STUDY RESULTS (#fafafa) */}
      <section className="relative bg-[#fafafa] py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(rgba(227,30,36,0.03)_1px,transparent_1px)] bg-[length:28px_28px]" />
        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                Proven Results
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-0 text-slate-900">
                Carriers scaling efficiently with <span className="text-[#ff5d22]">AppGallop</span>
              </h2>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {D.results.map((m, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <TiltCard className="bg-white border border-slate-200 rounded-2xl p-8 text-center relative overflow-hidden h-full shadow-sm">
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#ff5d22] to-red-400" />
                  <div className="text-4xl font-extrabold text-[#2563EB] mb-2">{m.val}</div>
                  <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">{m.label}</div>
                  <div className="text-xs text-slate-500 font-semibold leading-relaxed">{m.org}</div>
                </TiltCard>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.25}>
            <div className="bg-gradient-to-br from-slate-950 to-slate-900 rounded-2xl p-8 border border-white/8 relative overflow-hidden shadow-2xl">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22] to-transparent" />
              <div className="flex gap-4 items-start">
                <div className="w-10 h-10 rounded-lg bg-[#ff5d22]/20 border border-[#ff5d22]/30 flex items-center justify-center flex-shrink-0 mt-1">
                  <Icon d={icons.bar} size={18} style={{ color:'#ff5d22' }} />
                </div>
                <div>
                  <p className="text-slate-300 text-sm md:text-base leading-relaxed font-medium">
                    A prominent communications provider offering SmartFlo digital services optimized their active CDR processing pipelines. Consolidating voice minute mediatizing, cross-reseller provisioning logic, and downstream switch triggers instantly on AppGallop decreased monthly invoice queries by over 75%, lowered dispute closures, and dropped lead SLA activation from 3 days to minutes.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ACTIVATION (slate-950 dark) */}
      <section className="relative bg-[#08080c] py-20 px-6 overflow-hidden">
        <ParticleCanvas count={60} />
        <Orb style={{ top:'5%', left:'10%' }} color="#ff5d22" size={450} animDur={20} />
        <GridFloor opacity={0.05} />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[9px] font-black uppercase tracking-widest mb-4">
              Your Commerce Engine, Activated
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-white mb-4">
              Stop rebuilding rating models.{' '}
              <span className="text-[#ff5d22]">Launch and Scale.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="text-[#98a2b3] text-sm md:text-base leading-relaxed mb-8 max-w-xl mx-auto font-medium">
              Consolidate per-seat licensing and dynamic CDR trunks with complete financial integrity. Reclaim unbilled revenue leaks and elevate GTM speed with our telco-grade billing lattice.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/contact">
              <button className="px-8 py-3.5 rounded-lg bg-[#ff5d22] text-white font-black text-xs uppercase tracking-widest border-none cursor-pointer hover:bg-[#d41820] transition-all duration-300" style={{ animation:'pulse3d 3s ease infinite' }}>
                Book Consultation
              </button>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PLATFORM METRICS (white) */}
      <section className="relative bg-white py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(rgba(0,0,0,0.015)_1px,transparent_1px)] bg-[length:26px_26px]" />
        <Orb style={{ bottom:'0%', right:'0%' }} color="#ff5d22" size={300} animDur={21} />
        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                Platform Statistics
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-3 block text-slate-900">
                Engineered for <span className="text-[#ff5d22]">precision at scale</span>
              </h2>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {D.platformFeatures.map((item, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <TiltCard className="bg-white border border-slate-200 rounded-2xl p-6 text-center shadow-sm relative overflow-hidden h-full">
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#ff5d22]" />
                  <div className="text-3xl font-extrabold text-[#2563EB] mb-1.5">{item.val}</div>
                  <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">{item.label}</div>
                  <p className="text-xs text-slate-500 leading-normal font-semibold">{item.desc}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <div className="bg-gradient-to-br from-slate-950 to-slate-900 rounded-2xl p-10 text-center border border-white/6 relative overflow-hidden shadow-2xl">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22] to-transparent" />
              <div className="w-12 h-12 rounded-full bg-[#ff5d22]/20 border border-[#ff5d22]/35 flex items-center justify-center mx-auto mb-6">
                <Icon d={icons.msg} size={20} style={{ color:'#ff5d22' }} />
              </div>
              <blockquote className="text-base md:text-lg italic text-slate-300 font-bold max-w-2xl mx-auto leading-relaxed mb-4">
                "Consolidating voice tariffs, reseller payouts, and downstream numbers on AppGallop decreased monthly billing reconciliations to less than four hours with absolutely zero packet rating leakage."
              </blockquote>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                Finance Director, SmartFlo Operations (Tata Tele partner)
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative bg-[#fafafa] py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(rgba(227,30,36,0.02)_1px,transparent_1px)] bg-[length:28px_28px]" />
        <Orb style={{ top:'15%', left:'-8%' }} color="#ff5d22" size={280} animDur={19} />
        <div className="relative z-10 max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                FAQ
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-0">
                Frequently Asked Questions
              </h2>
            </Reveal>
          </div>
          {D.faq.map((item, i) => (
            <FAQItem key={i} q={item.q} a={item.a} idx={i} />
          ))}
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="relative bg-[#06060a] py-24 px-6 overflow-hidden">
        <ParticleCanvas count={90} />
        <GridFloor opacity={0.06} />
        <Orb style={{ top:'10%', left:'10%' }} color="#ff5d22" size={450} animDur={18} />
        <Orb style={{ bottom:'10%', right:'5%' }} color="#3b82f6" size={380} animDur={22} animDelay={4} />
        <div className="relative z-10 max-w-3xl mx-auto text-center space-y-6">
          <Reveal>
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#ff5d22]">
              Start Scaling Efficiently
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
              Turn your billing pipeline into a{' '}
              <span className="text-[#ff5d22]">revenue asset</span>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="text-slate-400 text-sm md:text-base leading-relaxed max-w-xl mx-auto">
              Schedule a comprehensive billing assessment with our engineers and launch your telecom converged logic within ten business days.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link to="/contact">
                <button className="px-8 py-3 rounded-lg bg-[#ff5d22] text-white font-black text-[10px] uppercase tracking-widest border-none cursor-pointer hover:bg-[#d41820] transition-all duration-300" style={{ animation:'pulse3d 3s ease infinite' }}>
                  Book Demo
                </button>
              </Link>
              <Link to="/solutions">
                <button className="px-8 py-3 rounded-lg bg-transparent text-slate-300 font-black text-[10px] uppercase tracking-widest border border-white/20 cursor-pointer hover:bg-white/5 hover:text-white transition-all">
                  View Case Studies
                </button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Contact Form Integration */}
      <div id="contact">
        <ContactSection />
      </div>
    </div>
  );
};
